import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { cn } from '@/lib/utils.ts';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import InputHint from '@/components/form/base/InputHint.tsx';

/**
 * Awaryjna barwa, gdy pole nie ma jeszcze wartości — `<input type="color">`
 * bez `value` przechodzi w tryb niekontrolowany. Wołający podaje własną
 * przez `fallbackColor`; prymityw formularza nie zna domyślnych żadnej domeny.
 */
const NEUTRAL_FALLBACK_COLOR = '#C4C4D4';

const ColorInput = <T extends FieldValues>({
  fieldPath,
  control,
  label,
  required = false,
  disabled = false,
  error,
  hint,
  className,
  fallbackColor = NEUTRAL_FALLBACK_COLOR,
  presets = [],
}: {
  fieldPath: FieldPath<T>;
  control: Control<T>;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  fallbackColor?: string;
  /** Podpowiedzi barw. Pole hex zostaje, próbki tylko skracają drogę. */
  presets?: string[];
}) => {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <InputLabel label={label} required={required} disabled={disabled} />
      )}
      <Controller
        control={control}
        name={fieldPath}
        render={({ field }) => (
          <div className="flex flex-col gap-2">
            {presets.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {presets.map((color) => (
                  <button
                    key={color}
                    type="button"
                    aria-label={`Kolor ${color}`}
                    aria-pressed={field.value === color}
                    disabled={disabled}
                    onClick={() => field.onChange(color)}
                    className={cn(
                      'size-7 rounded-full border-2 transition disabled:cursor-not-allowed disabled:opacity-50',
                      field.value === color
                        ? 'border-neutral-900'
                        : 'border-transparent hover:border-neutral-300',
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="color"
                disabled={disabled}
                value={field.value ?? fallbackColor}
                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                onBlur={field.onBlur}
                className="h-9 w-12 cursor-pointer rounded-sm border border-neutral-200 p-0.5 disabled:cursor-not-allowed"
              />
              <span className="font-mono text-sm uppercase text-neutral-700">
                {field.value ?? fallbackColor}
              </span>
            </div>
          </div>
        )}
      />
      {error && <InputError error={error} />}
      {hint && <InputHint hint={hint} />}
    </div>
  );
};

export default ColorInput;
